import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, Loader2, ArrowRight, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { usePassportEntries } from '@/hooks/usePassport';
import { useSavedDestinations } from '@/hooks/useSavedDestinations';
import { useSurfLevel, useSetSurfLevel, useAchievements } from '@/hooks/useSurfProgression';
import {
  getUniqueCountryCount,
  getHighlightedIso2Codes,
  getDestinationCountsByIso2,
  getRecentCountryRecords,
} from '@/lib/passport';
import { ACHIEVEMENTS_BY_LEVEL } from '@/lib/surf-progression';
import PassportWorldMap from '@/components/passport/PassportWorldMap';
import SurfProfileCard from '@/components/dashboard/SurfProfileCard';
import SetSurfLevelCard from '@/components/dashboard/SetSurfLevelCard';
import LastSurfTripCard from '@/components/dashboard/LastSurfTripCard';
import SurfingJourneyCard from '@/components/dashboard/SurfingJourneyCard';
import NextMilestoneCard from '@/components/dashboard/NextMilestoneCard';
import WhereNextCard from '@/components/dashboard/WhereNextCard';
import Reveal from '@/components/dashboard/Reveal';

const MySwellBreaks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { data: entries = [], isLoading: entriesLoading } = usePassportEntries();
  const { data: saved, isLoading: savedLoading } = useSavedDestinations();
  const { data: level, isLoading: levelLoading } = useSurfLevel();
  const { data: completedIds = new Set<string>(), isLoading: achievementsLoading } = useAchievements();
  const setLevelMutation = useSetSurfLevel();
  const progressionHydrated = !levelLoading && !achievementsLoading;

  if (loading) {
    return (
      <Layout>
        <Seo title="My Swell Breaks" description="Your personal Swell Breaks home." path="/my-swell-breaks" noIndex />
        <div className="container mx-auto px-4 py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <Seo title="My Swell Breaks" description="Your personal Swell Breaks home." path="/my-swell-breaks" noIndex />
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="font-serif text-3xl md:text-5xl font-semibold text-foreground mb-4">My Swell Breaks</h1>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Your Surf Passport and saved trips, in one place.
              </p>
            </div>

            <Card className="max-w-lg mx-auto border-border/50">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-serif text-2xl">Sign in to continue</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground text-center">
                  Create an account to build your Surf Passport and keep your saved trips close at hand.
                </p>
                <div className="flex flex-col gap-3 pt-2">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={() => navigate('/auth', { state: { from: location.pathname } })}
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/auth', { state: { from: location.pathname } })}
                  >
                    Sign In
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  const countryCount = getUniqueCountryCount(entries);
  const highlightedIso2 = getHighlightedIso2Codes(entries);
  const destinationCounts = getDestinationCountsByIso2(entries);
  const recentCountryRecords = getRecentCountryRecords(entries, 3);
  const savedPreview = (saved ?? []).slice(0, 3);
  const latestEntry = entries.length > 0 ? entries[0] : null; // usePassportEntries orders created_at desc

  const displayName = (user.user_metadata?.full_name as string | undefined)?.trim() || user.email || 'Surfer';
  const levelAchievedCount = level
    ? ACHIEVEMENTS_BY_LEVEL[level].filter((a) => !a.optional && completedIds.has(a.id)).length
    : 0;

  // Lands on /my-passport with its Add sheet already open -- a returning user should
  // be able to log somewhere they just surfed in one tap from here, not two.
  const handleAddToPassport = () => navigate('/my-passport', { state: { openAdd: true } });

  return (
    <Layout>
      <Seo title="My Swell Breaks" description="Your personal Swell Breaks home." path="/my-swell-breaks" noIndex />

      <section className="pt-8 pb-4 md:pt-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="font-serif text-2xl md:text-4xl font-semibold text-foreground">My Swell Breaks</h1>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-4xl space-y-6 md:space-y-8">
        {/* A. Surf Profile -- the hero component. Onboarding empty state
            (SetSurfLevelCard) shown until a level is self-selected, since
            sections C/D below depend on one. */}
        {!progressionHydrated ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !level ? (
          <SetSurfLevelCard onSelect={(next) => setLevelMutation.mutate(next)} />
        ) : (
          <Reveal>
            <SurfProfileCard
              displayName={displayName}
              level={level}
              levelAchievedCount={levelAchievedCount}
              onChangeLevel={(next) => setLevelMutation.mutate(next)}
            />
          </Reveal>
        )}
      </div>

      {/* B. Surf Passport -- let the map breathe: full-bleed tonal section,
          not another bordered card, so it reads as a defining feature
          immediately after the profile hero rather than a subsection. */}
      {entriesLoading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Reveal>
        <section className="mt-6 md:mt-8 border-y border-border/50 bg-secondary/30 py-8 md:py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Your Surf Passport</p>
            <p className="mt-1.5 font-serif text-2xl md:text-3xl text-foreground">
              {countryCount} {countryCount === 1 ? 'country' : 'countries'}. {entries.length} {entries.length === 1 ? 'destination' : 'destinations'}.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">Everywhere surfing has taken you.</p>

            <div className="-mx-4 sm:mx-0 mt-5 md:mt-6">
              <PassportWorldMap highlightedIso2={highlightedIso2} destinationCountsByIso2={destinationCounts} />
            </div>
            {entries.length === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">Your surf map starts here.</p>
            )}

            {recentCountryRecords.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {recentCountryRecords.map((record) => (
                  // data-country-iso2 is a stable hook for a future pass to
                  // wire hover-to-highlight-on-map / click-to-open-entry --
                  // not a real control yet, so it's not marked up as one
                  // (no focusable/interactive element with no behavior).
                  <span key={record.countryId} data-country-iso2={record.countryIso2 ?? undefined}>
                    <Badge variant="secondary" className="font-normal py-1.5 px-3">
                      {record.countryName} · {record.destinationName}
                    </Badge>
                  </span>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button onClick={handleAddToPassport} size="sm" className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Log Surf Trip
              </Button>
              <Link
                to="/my-passport"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                View Passport
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>
        </Reveal>
      )}

      {/* C. Last Surf Trip, D. Your Surfing Journey, E. Where Next, F. Next
          Milestone -- only once a level is set (most depend on one). Each
          uses a different visual treatment on purpose (journal rule / card
          with skill summary / milestone card / solid inspirational block)
          rather than repeating the same card shape. */}
      {progressionHydrated && level && (
        <div className="container mx-auto px-4 max-w-4xl mt-8 md:mt-10 space-y-8 md:space-y-10">
          {!entriesLoading && (
            <Reveal>
              <LastSurfTripCard latestEntry={latestEntry} />
            </Reveal>
          )}
          <Reveal>
            <SurfingJourneyCard level={level} completedIds={completedIds} />
          </Reveal>
          <Reveal>
            <NextMilestoneCard level={level} completedIds={completedIds} />
          </Reveal>
          <Reveal>
            <WhereNextCard />
          </Reveal>
        </div>
      )}

      <section className="py-10 md:py-14">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Saved Trips -- secondary */}
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-4">Saved Trips</p>

            {savedLoading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : savedPreview.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                You haven't saved any destinations yet.{' '}
                <Link to="/destinations" className="text-primary hover:underline">
                  Browse destinations
                </Link>
                .
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {savedPreview.map((row) => {
                  const d = row.destinations;
                  if (!d) return null;
                  return (
                    <Link
                      key={row.id}
                      to={`/destinations/${d.slug}`}
                      className="group relative overflow-hidden rounded-xl border border-border/40 bg-card/70"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={d.hero_image_url ?? ''}
                          alt={d.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-muted-foreground mb-0.5">{d.countries?.name}</p>
                        <h3 className="font-serif text-base text-foreground">{d.name}</h3>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            <Link
              to="/my-trips"
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              View My Trips
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MySwellBreaks;
